import React, { useEffect, useState } from 'react'

import { getListingDetails } from '../../../utilities/api'
import {
  EMPTY_DISPLAY_VALUE,
  formatCurrency,
  formatDateTime,
  formatNumber,
  formatTicketLocationValue,
} from '../../../utilities/helpers'

import Badge from 'react-bootstrap/Badge'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import { LoadingContainer } from '../../LoadingContainer'
import DetailList from './DetailList'

const LISTING_BADGE = {
  new: 'active',
  reserved: 'reserved',
  complete: 'complete',
  expired: 'ended',
}

const money = (value) =>
  value == null || value === '' ? EMPTY_DISPLAY_VALUE : formatCurrency(value)

const sellerValue = (seller) => {
  if (!seller?.name && !seller?.email) return EMPTY_DISPLAY_VALUE
  return (
    <div>
      <div>{seller.name || seller.email}</div>
      {seller.name && seller.email ? (
        <div className="text-muted small">{seller.email}</div>
      ) : null}
    </div>
  )
}

export default function ListingDetailsModal({ listingId, organizationUUID }) {
  const [listing, setListing] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    if (!listingId || !organizationUUID) {
      setIsLoading(false)
      setError('Listing details are unavailable.')
      return undefined
    }

    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await getListingDetails({
          id: listingId,
          organizationUUID,
        })
        if (!cancelled) setListing(res.data)
      } catch (err) {
        console.error(err)
        if (!cancelled) setError('Could not load listing details.')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [listingId, organizationUUID])

  const badgeValue = LISTING_BADGE[listing?.status] || listing?.status

  return (
    <>
      <Modal.Header closeButton className="border-0 pb-0">
        <Stack>
          <Modal.Title as="h4" className="h3 fw-bold">
            Listing Details
          </Modal.Title>
          <div className="text-muted mt-1">Listing ID: {listingId}</div>
        </Stack>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <LoadingContainer />
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : listing ? (
          <>
            <DetailList
              rows={[
                {
                  label: 'Status',
                  value: listing.statusLabel ? (
                    <Badge
                      bg="none"
                      className={`badge-sm badge--${badgeValue}`}
                    >
                      {listing.statusLabel}
                    </Badge>
                  ) : (
                    EMPTY_DISPLAY_VALUE
                  ),
                },
                {
                  label: 'Ticket type',
                  value: listing.ticketType || EMPTY_DISPLAY_VALUE,
                },
                { label: 'Asking price', value: money(listing.askingPrice) },
                {
                  label: 'Quantity',
                  value: formatNumber(listing.quantity),
                },
                { label: 'Payout', value: money(listing.payout) },
                { label: 'Seller fee', value: money(listing.serviceFees) },
                { label: 'Seller', value: sellerValue(listing.seller) },
                {
                  label: 'From order',
                  value: listing.fromOrderId || EMPTY_DISPLAY_VALUE,
                },
                {
                  label: 'Buyer order',
                  value: listing.buyerOrderId || EMPTY_DISPLAY_VALUE,
                },
                {
                  label: 'Funds claimed',
                  value: (
                    <Badge
                      bg="none"
                      className={`badge-sm badge--${
                        listing.fundsClaimed ? 'paid' : 'released'
                      }`}
                    >
                      {listing.fundsClaimed ? 'Yes' : 'No'}
                    </Badge>
                  ),
                },
                listing.claimedOn
                  ? {
                      label: 'Claimed on',
                      value:
                        formatDateTime(listing.claimedOn) ||
                        EMPTY_DISPLAY_VALUE,
                    }
                  : null,
              ]}
            />
            <h5 className="mt-4 mb-2">Tickets</h5>
            {listing.tickets?.length ? (
              <div className="table-container border listing-tickets">
                <Table className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="text-left">Ticket</th>
                      <th className="text-left">Section</th>
                      <th className="text-left">Row</th>
                      <th className="text-left">Seat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listing.tickets.map((ticket) => {
                      const fallback = ticket.generalAdmission
                        ? 'GA'
                        : EMPTY_DISPLAY_VALUE
                      return (
                        <tr key={ticket.id}>
                          <td className="text-left">
                            {ticket.name || EMPTY_DISPLAY_VALUE}
                          </td>
                          <td className="text-left">
                            {formatTicketLocationValue(
                              ticket.sectionNumber,
                              fallback
                            )}
                          </td>
                          <td className="text-left">
                            {formatTicketLocationValue(
                              ticket.rowNumber,
                              fallback
                            )}
                          </td>
                          <td className="text-left">
                            {formatTicketLocationValue(
                              ticket.seatNumber,
                              fallback
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p className="text-muted mb-0">No tickets on this listing.</p>
            )}
          </>
        ) : (
          <div>No listing data found.</div>
        )}
      </Modal.Body>
    </>
  )
}
